import { useState } from "react";
import { api, apiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, SectionTitle } from "@/components/dash";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ImageUpload } from "@/components/ImageUpload";
import { ACCENT_COLORS } from "@/lib/constants";
import { CURATED_COVERS } from "@/lib/branding";
import { Loader2, ExternalLink, Copy, Eye } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function StorefrontSettings() {
  const { bakery, refresh } = useAuth();
  const [form, setForm] = useState({ ...bakery });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const storeUrl = `${window.location.origin}/bakery/${bakery.slug}`;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/bakery", {
        name: form.name, description: form.description, accent_color: form.accent_color,
        slug: form.slug, additional_info: form.additional_info, payment_instructions: form.payment_instructions,
        contact_email: form.contact_email, logo_path: form.logo_path, cover_path: form.cover_path,
        logo_mode: form.logo_mode || (form.logo_path ? "image" : "monogram"),
        logo_monogram: form.logo_monogram || null, logo_bg_color: form.logo_bg_color || null, logo_text_color: form.logo_text_color || null,
        cover_mode: form.cover_mode || (form.cover_path ? "image" : "none"), cover_color: form.cover_color || null,
      });
      await refresh();
      toast.success("Storefront updated");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const togglePublish = async (v) => {
    try {
      await api.put("/bakery", { published: v });
      await refresh();
      setForm((f) => ({ ...f, published: v }));
      toast.success(v ? "Storefront published!" : "Storefront unpublished");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <SectionTitle>Storefront</SectionTitle>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-serif text-lg text-cocoa">{form.published ? "Your storefront is live" : "Your storefront is hidden"}</p>
            <p className="text-sm text-cocoa/60">{form.published ? "Customers can view and order from your bakery." : "Publish to let customers place orders."}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-cocoa/60">Published</span>
            <Switch checked={!!form.published} onCheckedChange={togglePublish} data-testid="publish-toggle" />
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2 rounded-xl bg-cream px-4 py-3">
          <span className="flex-1 truncate text-sm text-cocoa/70">{storeUrl}</span>
          <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(storeUrl); toast.success("Copied!"); }} className="rounded-full text-cocoa" data-testid="copy-link-btn"><Copy className="mr-1 h-3.5 w-3.5" /> Copy</Button>
          <Link to={`/bakery/${bakery.slug}?preview=1`} target="_blank"><Button variant="ghost" size="sm" className="rounded-full text-cocoa" data-testid="preview-storefront-btn"><Eye className="mr-1 h-3.5 w-3.5" /> Preview</Button></Link>
        </div>
      </Card>

      <Card>
        <BrandingEditor form={form} set={set} />
        <div className="mt-6 space-y-4">
          <div><Label className="text-cocoa">Bakery name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 rounded-xl bg-cream" data-testid="storefront-name" /></div>
          <div><Label className="text-cocoa">Description</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5 rounded-xl bg-cream" rows={3} data-testid="storefront-description" /></div>
          <div>
            <Label className="text-cocoa">Storefront link (/bakery/...)</Label>
            <Input value={form.slug} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-"))} className="mt-1.5 rounded-xl bg-cream" data-testid="storefront-slug" />
          </div>
          <div>
            <Label className="text-cocoa">Accent color</Label>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {ACCENT_COLORS.map((c) => (
                <button key={c} type="button" onClick={() => set("accent_color", c)} className={`h-9 w-9 rounded-full transition-transform ${form.accent_color === c ? "ring-2 ring-cocoa ring-offset-2 ring-offset-warmwhite scale-110" : ""}`} style={{ backgroundColor: c }} data-testid={`accent-${c}`} />
              ))}
              <input type="color" value={form.accent_color} onChange={(e) => set("accent_color", e.target.value)} className="h-9 w-9 cursor-pointer rounded-full border-0 bg-transparent" data-testid="accent-custom" />
            </div>
          </div>
          <div><Label className="text-cocoa">Additional information</Label><Textarea value={form.additional_info || ""} onChange={(e) => set("additional_info", e.target.value)} className="mt-1.5 rounded-xl bg-cream" rows={2} data-testid="storefront-additional-info" placeholder="General bakery info — e.g. special-order contact, regular market stand. Not the pickup instructions for a specific order." /><p className="mt-1 text-xs text-cocoa/50">Shown in your storefront's general info area. Each bake day sets its own pickup instructions.</p></div>
          <div><Label className="text-cocoa">Payment instructions</Label><Textarea value={form.payment_instructions} onChange={(e) => set("payment_instructions", e.target.value)} className="mt-1.5 rounded-xl bg-cream" rows={2} data-testid="storefront-payment" /></div>
          <div><Label className="text-cocoa">Contact email</Label><Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} className="mt-1.5 rounded-xl bg-cream" data-testid="storefront-contact" /></div>
          <Button onClick={save} disabled={saving} className="rounded-full bg-terracotta px-8 py-6 text-warmwhite hover:bg-terracotta/90" data-testid="save-storefront-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button>
        </div>
      </Card>
    </div>
  );
}

function ModeBtn({ active, onClick, children, testId }) {
  return <button type="button" onClick={onClick} data-testid={testId} className={`rounded-full px-3 py-1.5 text-sm transition-colors ${active ? "bg-terracotta text-warmwhite" : "bg-cream text-cocoa hover:bg-cocoa/5"}`}>{children}</button>;
}

function BrandingEditor({ form, set }) {
  const logoMode = form.logo_mode || (form.logo_path ? "image" : "monogram");
  const coverMode = form.cover_mode || (form.cover_path ? "image" : "none");
  const mono = form.logo_monogram || (form.name || "B").trim().charAt(0).toUpperCase();
  const bg = form.logo_bg_color || "#BD806A";
  const fg = form.logo_text_color || "#FFF8F0";
  return (
    <div className="grid gap-6 md:grid-cols-2">
      <div>
        <Label className="text-cocoa">Logo</Label>
        <div className="mb-3 mt-1.5 flex gap-2">
          <ModeBtn active={logoMode === "image"} onClick={() => set("logo_mode", "image")} testId="logo-mode-image">Image</ModeBtn>
          <ModeBtn active={logoMode === "monogram"} onClick={() => set("logo_mode", "monogram")} testId="logo-mode-monogram">Monogram</ModeBtn>
          <ModeBtn active={logoMode === "none"} onClick={() => set("logo_mode", "none")} testId="logo-mode-none">None</ModeBtn>
        </div>
        {logoMode === "image" && <ImageUpload label="" value={form.logo_path && !String(form.logo_path).startsWith("http") ? form.logo_path : null} onChange={(v) => set("logo_path", v)} testId="logo-upload" aspect="aspect-square" hint="Square image works best. Up to 8MB." />}
        {logoMode === "monogram" && (
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full text-2xl font-semibold" style={{ backgroundColor: bg, color: fg }} data-testid="logo-monogram-preview">{mono}</div>
            <div className="space-y-2">
              <Input maxLength={2} value={mono} onChange={(e) => set("logo_monogram", e.target.value.toUpperCase().slice(0, 2))} className="w-20 rounded-xl bg-cream" data-testid="logo-monogram-input" />
              <div className="flex items-center gap-3 text-xs text-cocoa/60">
                <label className="flex items-center gap-1">Bg <input type="color" value={bg} onChange={(e) => set("logo_bg_color", e.target.value)} className="h-6 w-6 rounded border-0 bg-transparent" data-testid="logo-bg-color" /></label>
                <label className="flex items-center gap-1">Text <input type="color" value={fg} onChange={(e) => set("logo_text_color", e.target.value)} className="h-6 w-6 rounded border-0 bg-transparent" data-testid="logo-text-color" /></label>
              </div>
            </div>
          </div>
        )}
        {logoMode === "none" && <p className="text-sm text-cocoa/50">No logo will be shown.</p>}
      </div>
      <div>
        <Label className="text-cocoa">Cover image</Label>
        <div className="mb-3 mt-1.5 flex flex-wrap gap-2">
          <ModeBtn active={coverMode === "image"} onClick={() => set("cover_mode", "image")} testId="cover-mode-image">Image</ModeBtn>
          <ModeBtn active={coverMode === "color"} onClick={() => set("cover_mode", "color")} testId="cover-mode-color">Color</ModeBtn>
          <ModeBtn active={coverMode === "curated"} onClick={() => set("cover_mode", "curated")} testId="cover-mode-curated">Curated</ModeBtn>
          <ModeBtn active={coverMode === "none"} onClick={() => set("cover_mode", "none")} testId="cover-mode-none">None</ModeBtn>
        </div>
        {coverMode === "image" && <ImageUpload label="" value={form.cover_path && !String(form.cover_path).startsWith("http") ? form.cover_path : null} onChange={(v) => set("cover_path", v)} testId="cover-upload" hint="Wide image works best. Up to 8MB." />}
        {coverMode === "color" && (
          <div className="flex items-center gap-3">
            <div className="h-20 flex-1 rounded-xl" style={{ backgroundColor: form.cover_color || "#EFE6D9" }} />
            <input type="color" value={form.cover_color || "#EFE6D9"} onChange={(e) => set("cover_color", e.target.value)} className="h-10 w-10 cursor-pointer rounded-full border-0 bg-transparent" data-testid="cover-color" />
          </div>
        )}
        {coverMode === "curated" && (
          <div className="grid grid-cols-2 gap-2" data-testid="cover-curated-grid">
            {CURATED_COVERS.map((c) => (
              <button key={c.id} type="button" onClick={() => set("cover_path", c.url)} className={`overflow-hidden rounded-xl border-2 transition-colors ${form.cover_path === c.url ? "border-terracotta" : "border-transparent hover:border-cocoa/20"}`} data-testid={`cover-curated-${c.id}`}>
                <img src={c.url} alt={c.label} className="aspect-video w-full object-cover" />
              </button>
            ))}
          </div>
        )}
        {coverMode === "none" && <p className="text-sm text-cocoa/50">No cover image will be shown.</p>}
      </div>
    </div>
  );
}
